import { getConfig } from '../config';

export interface TriggerBatchResponse {
  jobId: string;
  status?: string;
  submitted?: boolean;
  message?: string;
}

export interface BatchJob {
  jobId: string;
  jobName: string;
  status: string;
  createdAt: string;
  startedAt: string;
  stoppedAt?: string;
  statusReason?: string;
}

export interface BatchJobsResponse {
  jobs: BatchJob[];
  nextToken?: string;
}

export interface BatchJobDetailResponse {
  job: BatchJob;
}

export type JobStatus = 'SUCCEEDED' | 'FAILED' | 'RUNNING' | 'PENDING' | 'SUBMITTED' | 'RUNNABLE' | 'STARTING';

export enum CommandName {
  MATCH_SKU = 'MatchSKU',
  EXPORT_SKU_MATCHED = 'ExportSKUMatched',
  EXPORT_CP_GP_MATCHED = 'ExportCpGpMatched',
  SCRAPE_COUPANG = 'ScrapeCoupang',
  GENERATE_GOOGLE_PRODUCT_FEED = 'GenerateGoogleProductFeed',
  GENERATE_META_CATALOG_FEED = 'GenerateMetaCatalogFeed',
}

export interface BatchJobTriggerData {
  command: string;
  args: Record<string, any>;
}

export interface ExportToGoogleSheetArgs {
  sheetId: string;
  sheetName: string;
}

export interface ExportCpGpMatchedArgs {
  sheetId: string;
  sheetName: string;
  limit?: number;
  category?: string;
  subCategories?: string[];
}

export interface ScrapeCoupangArgs {
  task: string;
  gpProductIds?: string[];
  forceRewrite?: boolean;
  category?: string;
  subcategory?: string;
  refresh?: boolean;
  limit?: number;
}

export class BatchClient {
  private readonly headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-API-Key': getConfig().apiKey,
  };

  private readonly apiUrl: string;

  constructor(baseUrl: string) {
    // Ensure HTTPS protocol to avoid redirect issues with CORS preflight
    if (!baseUrl.includes('localhost')) {
      this.apiUrl = baseUrl.replace('http://', 'https://');
    } else {
      this.apiUrl = baseUrl;
    }
  }

  public async triggerAIMatchBatchJob(): Promise<TriggerBatchResponse> {
    const response = await fetch(`${this.apiUrl}/batch/trigger`, {
      method: 'POST',
      headers: this.headers,
      mode: 'cors',
      body: JSON.stringify({
        command: CommandName.MATCH_SKU,
        args: {},
      } as BatchJobTriggerData),
    });

    if (!response.ok) {
      throw new Error(`Failed to trigger batch job: ${response.statusText}`);
    }

    return response.json();
  }

  public async triggerExportToGoogleSheetJob(args: ExportToGoogleSheetArgs): Promise<TriggerBatchResponse> {
    const response = await fetch(`${this.apiUrl}/batch/trigger`, {
      method: 'POST',
      headers: this.headers,
      mode: 'cors',
      body: JSON.stringify({
        command: CommandName.EXPORT_SKU_MATCHED,
        args,
      } as BatchJobTriggerData),
    });

    if (!response.ok) {
      throw new Error(`Failed to trigger export job: ${response.statusText}`);
    }

    return response.json();
  }

  public async triggerExportCpGpMatchedJob(args: ExportCpGpMatchedArgs): Promise<TriggerBatchResponse> {
    const response = await fetch(`${this.apiUrl}/batch/trigger`, {
      method: 'POST',
      headers: this.headers,
      mode: 'cors',
      body: JSON.stringify({
        command: CommandName.EXPORT_CP_GP_MATCHED,
        args,
      } as BatchJobTriggerData),
    });

    if (!response.ok) {
      throw new Error(`Failed to trigger ExportCpGpMatched job: ${response.statusText}`);
    }

    return response.json();
  }

  public async listBatchJobs(options?: {
    status?: JobStatus | 'all';
    commandName?: CommandName;
  }): Promise<BatchJobsResponse> {
    const queryParams = new URLSearchParams();

    if (options?.status && options.status !== 'all') {
      queryParams.append('status', options.status);
    } else if (options?.status === 'all') {
      queryParams.append('status', 'all');
    }

    if (options?.commandName) {
      queryParams.append('commandName', options.commandName);
    }

    try {
      const response = await fetch(`${this.apiUrl}/batch/jobs?${queryParams.toString()}`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to list batch jobs: ${response.statusText}`);
      }

      return this.parseJsonResponse<BatchJobsResponse>(response);
    } catch (error) {
      console.error('Error fetching batch jobs:', error);
      throw error;
    }
  }

  public async triggerScrapeCoupangJob(args: ScrapeCoupangArgs): Promise<TriggerBatchResponse> {
    const response = await fetch(`${this.apiUrl}/batch/trigger`, {
      method: 'POST',
      headers: this.headers,
      mode: 'cors',
      body: JSON.stringify({
        command: CommandName.SCRAPE_COUPANG,
        args,
      } as BatchJobTriggerData),
    });

    if (!response.ok) {
      throw new Error(`Failed to trigger ScrapeCoupang job: ${response.statusText}`);
    }

    const body = await this.parseJsonResponse<TriggerBatchResponse>(response);

    if (body.submitted === false || body.status === 'FAILED') {
      throw new Error(body.message || 'Failed to trigger ScrapeCoupang job');
    }

    if (!body.jobId) {
      throw new Error(body.message || 'ScrapeCoupang trigger did not return a job ID');
    }

    return body;
  }

  public async getBatchJobStatus(jobId: string): Promise<BatchJobDetailResponse> {
    const response = await fetch(`${this.apiUrl}/batch/jobs/${jobId}`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });

    if (!response.ok) {
      throw new Error(`Failed to get batch job status: ${response.statusText}`);
    }

    return this.parseJsonResponse<BatchJobDetailResponse>(response);
  }

  public async triggerGenerateGoogleProductFeedJob(): Promise<TriggerBatchResponse> {
    const response = await fetch(`${this.apiUrl}/batch/trigger`, {
      method: 'POST',
      headers: this.headers,
      mode: 'cors',
      body: JSON.stringify({
        command: CommandName.GENERATE_GOOGLE_PRODUCT_FEED,
        args: {},
      } as BatchJobTriggerData),
    });

    if (!response.ok) {
      throw new Error(`Failed to trigger Google Product Feed job: ${response.statusText}`);
    }

    return response.json();
  }

  public async triggerGenerateMetaCatalogFeedJob(): Promise<TriggerBatchResponse> {
    const response = await fetch(`${this.apiUrl}/batch/trigger`, {
      method: 'POST',
      headers: this.headers,
      mode: 'cors',
      body: JSON.stringify({
        command: CommandName.GENERATE_META_CATALOG_FEED,
        args: {},
      } as BatchJobTriggerData),
    });

    if (!response.ok) {
      throw new Error(`Failed to trigger Meta Catalog Feed job: ${response.statusText}`);
    }

    return response.json();
  }

  private async parseJsonResponse<T>(response: Response): Promise<T> {
    return response.json() as Promise<T>;
  }
}
